import {
  cacheKeys,
  FIND_PRODUCT_ASSIGNMENTS,
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
} from '@gripp/shared-logic';
import {
  Colors,
  LoadingIndicator,
  SaveFormTop,
  SearchInput,
  TopModalBar,
  useGetDimensions,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import '../../shared/style/reactTabStyle.css';
import { ProductAddonsAssignList } from './productAddonsAssignList';
import { ProductFilesAssignList } from './productFilesAssignList';
import { ProductRoutinesAssignList } from './productRoutinesAssignList';

const ASSIGN_ROUTINES_TO_PRODUCT = `
  mutation AssignRoutinesToProduct($input: AssignToProductInput!) {
    product { assignToProduct(input: $input) { success message } }
  }
`;
const ASSIGN_FILES_TO_PRODUCT = `
  mutation AssignFilesToProduct($input: AssignToProductInput!) {
    product { assignToProduct(input: $input) { success message } }
  }
`;
const ASSIGN_ADDONS_TO_PRODUCT = `
  mutation AssignAddonsToProduct($input: AssignToProductInput!) {
    product { assignToProduct(input: $input) { success message } }
  }
`;

export enum ProductAssignmentType {
  Routines = 'routines',
  Files = 'files',
  Addons = 'addons',
}

interface ProductAssignmentFormProps {
  productId: string;
  title: string;
  assignmentType: ProductAssignmentType;
  showTabs?: boolean;
  onComplete: () => void;
}

export const ProductAssignmentForm = ({
  productId,
  title,
  assignmentType,
  showTabs = true,
  onComplete,
}: ProductAssignmentFormProps) => {
  const { t } = useTranslation();
  const [tabIndex, setTabIndex] = useState(0);
  const tabMap = [
    ProductAssignmentType.Routines,
    ProductAssignmentType.Files,
    ProductAssignmentType.Addons,
  ];
  const [mode, setMode] = useState<ProductAssignmentType>(assignmentType);
  const isRoutines = mode === ProductAssignmentType.Routines;
  const isFiles = mode === ProductAssignmentType.Files;
  const isAddons = mode === ProductAssignmentType.Addons;

  const [routineAssignments, setRoutineAssignments] = useState<
    string[] | undefined
  >();
  const [fileAssignments, setFileAssignments] = useState<
    string[] | undefined
  >();
  const [addonAssignments, setAddonAssignments] = useState<
    string[] | undefined
  >();
  const [originalRoutines, setOriginalRoutines] = useState<
    string[] | undefined
  >();
  const [originalFiles, setOriginalFiles] = useState<string[] | undefined>();
  const [originalAddons, setOriginalAddons] = useState<string[] | undefined>();
  const [keyword, setKeyword] = useState('');

  const { data: assignments, isPending: assignmentsLoading } = useCustomQuery({
    query: FIND_PRODUCT_ASSIGNMENTS,
    cacheKey: ['product', 'assignments', productId],
    variables: { productId },
  });

  const {
    mutateAsync: assignRoutinesToProduct,
    isPending: isRoutinesSubmitting,
  } = useCustomMutation({ query: ASSIGN_ROUTINES_TO_PRODUCT });

  const { mutateAsync: assignFilesToProduct, isPending: isFilesSubmitting } =
    useCustomMutation({ query: ASSIGN_FILES_TO_PRODUCT });

  const { mutateAsync: assignAddonsToProduct, isPending: isAddonsSubmitting } =
    useCustomMutation({ query: ASSIGN_ADDONS_TO_PRODUCT });

  const workspace = useCurrentWorkspace(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (assignments && !assignmentsLoading) {
      const r = assignments.product.findProductAssignments.routines;
      setRoutineAssignments(r);
      setOriginalRoutines(r);
      const f = assignments.product.findProductAssignments.files;
      setFileAssignments(f);
      setOriginalFiles(f);
      const a = assignments.product.findProductAssignments.addons;
      setAddonAssignments(a);
      setOriginalAddons(a);
    }
  }, [assignments, assignmentsLoading]);

  useEffect(() => {
    let idx = 0;
    switch (assignmentType) {
      case ProductAssignmentType.Routines:
        idx = 0;
        break;
      case ProductAssignmentType.Files:
        idx = 1;
        break;
      case ProductAssignmentType.Addons:
        idx = 2;
        break;
    }
    setTabIndex(idx);
    setMode(assignmentType);
  }, [assignmentType]);

  useEffect(() => {
    setKeyword('');
  }, [assignmentType, mode, tabIndex]);

  const handleRoutinesSubmit = async () => {
    const input = {
      productId,
      workspaceId: workspace.id,
      routineIds: routineAssignments,
    } as any;
    await assignRoutinesToProduct({ input } as any, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: cacheKeys('product').all,
          exact: false,
        });
      },
    });
    onComplete();
  };

  const handleFilesSubmit = async () => {
    const input = {
      productId,
      workspaceId: workspace.id,
      fileIds: fileAssignments,
    } as any;
    await assignFilesToProduct({ input } as any, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: cacheKeys('product').all,
          exact: false,
        });
      },
    });
    onComplete();
  };

  const handleAddonsSubmit = async () => {
    const input = {
      productId,
      workspaceId: workspace.id,
      addonIds: addonAssignments,
    } as any;
    await assignAddonsToProduct({ input } as any, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: cacheKeys('product').all,
          exact: false,
        });
      },
    });
    onComplete();
  };

  const isDirty = () => {
    if (isRoutines) {
      return (
        originalRoutines &&
        routineAssignments &&
        !(
          originalRoutines.length === routineAssignments.length &&
          originalRoutines.every((id) => routineAssignments.includes(id)) &&
          routineAssignments.every((id) => originalRoutines.includes(id))
        )
      );
    }
    if (isFiles) {
      return (
        originalFiles &&
        fileAssignments &&
        !(
          originalFiles.length === fileAssignments.length &&
          originalFiles.every((id) => fileAssignments.includes(id)) &&
          fileAssignments.every((id) => originalFiles.includes(id))
        )
      );
    }
    if (isAddons) {
      return (
        originalAddons &&
        addonAssignments &&
        !(
          originalAddons.length === addonAssignments.length &&
          originalAddons.every((id) => addonAssignments.includes(id)) &&
          addonAssignments.every((id) => originalAddons.includes(id))
        )
      );
    }
    return false;
  };

  const { dimensions, onLayout } = useGetDimensions();
  const height = dimensions?.height ?? 0;
  const listMaxHeight = height - 50;

  return (
    <SaveFormTop
      onSubmit={
        isRoutines
          ? handleRoutinesSubmit
          : isFiles
          ? handleFilesSubmit
          : handleAddonsSubmit
      }
      title={title}
      topBar={TopModalBar}
      onCancel={onComplete}
      isDirty={isDirty()}
    >
      {assignmentsLoading ||
      (isRoutines && isRoutinesSubmitting) ||
      (isFiles && isFilesSubmitting) ||
      (isAddons && isAddonsSubmitting) ? (
        <LoadingIndicator flexContainer />
      ) : (
        <View style={styles.container}>
          <SearchInput
            value={keyword}
            onChangeText={setKeyword}
            onDebounce={setKeyword}
            debounceLength={3}
            backgroundColor={Colors.white}
            placeholder={t(
              `product.assignments.${
                isRoutines
                  ? 'routineSearchPlaceholder'
                  : isFiles
                  ? 'fileSearchPlaceholder'
                  : 'addonSearchPlaceholder'
              }`
            )}
            height={44}
          />
          <View style={styles.assignmentContainer} onLayout={onLayout}>
            <Tabs
              selectedIndex={tabIndex}
              onSelect={(i) => {
                setTabIndex(i);
                setMode(tabMap[i]);
              }}
            >
              {
                <TabList
                  style={{
                    display: showTabs ? 'flex' : 'none',
                  }}
                >
                  <Tab>{t('product.assignments.routines')}</Tab>
                  <Tab>{t('product.assignments.files')}</Tab>
                  <Tab>{t('product.assignments.addons')}</Tab>
                </TabList>
              }
              <View style={styles.listContainer}>
                <TabPanel>
                  {routineAssignments && workspace?.id && (
                    <ProductRoutinesAssignList
                      productId={productId}
                      workspaceId={workspace.id}
                      keyword={keyword}
                      maxHeight={listMaxHeight}
                      assignments={routineAssignments}
                      updateAssignments={setRoutineAssignments}
                    />
                  )}
                </TabPanel>
                <TabPanel>
                  {fileAssignments && workspace?.id && (
                    <ProductFilesAssignList
                      productId={productId}
                      workspaceId={workspace.id}
                      keyword={keyword}
                      maxHeight={listMaxHeight}
                      assignments={fileAssignments}
                      updateAssignments={setFileAssignments}
                    />
                  )}
                </TabPanel>
                <TabPanel>
                  {addonAssignments && workspace?.id && (
                    <ProductAddonsAssignList
                      productId={productId}
                      workspaceId={workspace.id}
                      keyword={keyword}
                      maxHeight={listMaxHeight}
                      assignments={addonAssignments}
                      updateAssignments={setAddonAssignments}
                    />
                  )}
                </TabPanel>
              </View>
            </Tabs>
          </View>
        </View>
      )}
    </SaveFormTop>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 20,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  assignmentContainer: {
    flex: 1,
    marginTop: 20,
  },
  listContainer: {
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    borderBottomLeftRadius: 12,
    overflow: 'hidden',
  },
});
